/* eslint no-process-env: 0 */

import { env } from '@zopauk/nextjs-env';
import { staticTheme } from '@/utils/theme';

// Import the themes css file
import '../themes/jl-money/theme.css';
import '../global.css';
import Head from 'next/head';
import Script from 'next/script';
import { useState } from 'react';
import { useTheme } from '@/themes';
import { QuopaStyles } from '@/styles';
import { logger } from '@/utils/logger';
import type { AppProps } from 'next/app';
import Loading from '@/components/Loading';
import type { NextPageContext } from 'next';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { CookiesProvider } from 'react-cookie';
import { Amplitude } from '@/components/Amplitude';
import { Honeycomb } from '@zopauk/libs-otel/client';
import { SessionIdProvider } from '@/context/sessionId';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppGlobalStyles } from '@/components/AppGlobalStyles';
import { AnonymousProvider } from '@/context/anonymousProvider';
import { MaintenanceGuard } from '@/components/MaintenanceGuard';
import { SessionProvider, Environments } from '@zopauk/nextjs-auth';
import { SessionExpiryModal } from '@/components/SessionExpiryModal';
import { ApplicationFormContextProvider } from '@/context/appContext';
import { setMembershipApiConfig } from '@/utils/api/membership-client';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { googleTagManagerRawScript } from '@zopauk/react-third-party-scripts';
import { ThemeProvider, GlobalStyles, Modal } from '@zopauk/react-components';
import InformationBanner from '@/components/InformationBanner/InformationBanner';

interface ExtendedAppProps extends AppProps {
  err?: NextPageContext['err'];
}

setMembershipApiConfig();

function App({ pageProps: { session, ...pageProps }, Component, err }: ExtendedAppProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: Number.parseInt(env('NEXT_PUBLIC_REACT_QUERY_RETRIES') || '3', 10),
            refetchOnWindowFocus: false,
            staleTime: 90_000, // 1.5 minutes
          },
          mutations: {
            retry: false, // Don't retry mutations - security codes are single-use
          },
        },
      }),
  );

  // TODO: What is the ID for GTM - we should consider this part of the theme?
  const { partnerName, theme, font } = useTheme();
  const serviceName = partnerName === 'jl-money' ? 'johnlewis-quopa-web' : 'quopa-web';

  Modal.setAppElement('body');

  return (
    <ErrorBoundary>
      <Head>
        <meta content="width=device-width, initial-scale=1, shrink-to-fit=no" name="viewport" />
      </Head>
      <div className={`${font.className} ${partnerName}`}>
        <CookiesProvider>
          <AnonymousProvider>
            <ThemeProvider theme={theme}>
              <SessionIdProvider>
                <QueryClientProvider client={queryClient}>
                  <GlobalStyles />
                  <QuopaStyles />
                  <AppGlobalStyles font={font} />
                  <Modal.Styles />
                  <SessionProvider
                    basePath={`${staticTheme().basePath}/api/auth`}
                    session={session}
                    ExpiryModal={SessionExpiryModal}
                    Loading={Loading}
                  >
                    <Navbar />
                    <InformationBanner cookieName="maintenance-banner" />
                    <MaintenanceGuard>
                      <ApplicationFormContextProvider>
                        <main>
                          <Component {...pageProps} err={err} />
                        </main>
                        <Footer />
                      </ApplicationFormContextProvider>
                      <Amplitude />
                    </MaintenanceGuard>
                  </SessionProvider>
                </QueryClientProvider>
              </SessionIdProvider>
            </ThemeProvider>
          </AnonymousProvider>
        </CookiesProvider>
        <Script id="google-tag-manager-script" strategy="lazyOnload">
          {googleTagManagerRawScript(staticTheme().gtm)}
        </Script>
        {['production', 'uat'].includes(env('NEXT_PUBLIC_APP_ENV') || '') ? (
          <Honeycomb
            honeycombOptions={{
              resourceAttributes: {
                theme: partnerName,
              },
            }}
            serviceVersion={process.env.NEXT_PUBLIC_APP_VERSION || ''}
            environment={env('NEXT_PUBLIC_APP_ENV') as Environments}
            token={env('NEXT_PUBLIC_HONEYCOMB_API_KEY') || ''}
            serviceName={serviceName}
            logger={logger}
          />
        ) : null}
      </div>
    </ErrorBoundary>
  );
}
export default App;
